import {
  createTranslationMessages,
  createTranslationRef,
} from '@backstage/frontend-plugin-api';
import { TranslationBlueprint } from '@backstage/plugin-app-react';

/**
 * Les messages par défaut (anglais) du module Odoo : le rapport du job
 * d'init (carte « Modules » et bandeaux), les identifiants (onglet Accès),
 * l'Odoo shell, le badge de version et les mots d'Odoo de l'onglet
 * Configuration (étape 4 du plan « généricité »). Séparés de
 * `projectEnvironments/translation.ts` à l'étape 9d du plan « plateforme
 * d'applications », clés et textes inchangés ; les mots communs à la page
 * d'une branche (`format.locale`, `error.noIdentity`, `initReport.jobLog`…)
 * y restent, lus par ce module dans le registre du cœur.
 */
export const odooTranslationRef = createTranslationRef({
  id: 'odoo',
  messages: {
    // The Odoo shell of the pods card (useOdooShell.ts), chart 0.9.8 and later.
    'shell.open': 'Open an Odoo shell',
    'shell.starting': 'Starting the shell…',
    'shell.help':
      'Type odoo-shell to open the Odoo shell. Nothing is saved without env.cr.commit(). The pod stops after 30 min without a session.',
    'shell.stopping': 'Stopping…',
    'shell.stopTooltip': 'Stop the Odoo shell',
    'shell.stopFor': 'Stop the Odoo shell {{pod}}',
    'shell.error.forbidden':
      'You are not allowed to start or stop the Odoo shell of this environment.',
    'shell.error.missing':
      'This environment has no Odoo shell: its chart is older than 0.9.8.',
    'shell.error.startFailed':
      'The Odoo shell could not be started. Try again in a moment.',
    'shell.error.stopFailed':
      'The Odoo shell could not be stopped. Try again in a moment.',
    'shell.error.stopTimeout':
      'The Odoo shell pod is still there 1 minute after the stop. Refresh the page in a moment.',
    'shell.error.timeout':
      'The Odoo shell is still not ready after 2 minutes. Check the “Needs a look” card, then try again.',
    'report.shell': 'Odoo shell',
    // The Odoo version badge (OdooVersionBadge.tsx): the series, then the
    // build date and the version as Odoo states it, in its tooltip.
    'odooVersion.label': 'Odoo {{series}}',
    'odooVersion.build': 'Build of {{date}} ({{raw}})',
    // The project page header's metadata row: the label of the Odoo version.
    'odooVersion.headerLabel': 'Version',
    // The « Modules » card of the overview (InitReportCard.tsx): the report
    // of the last run of the Odoo chart's init job (chart 0.9.6 and later).
    // `age` and `duration`: kubectl's compact notation (compactDuration).
    // `n`: a count, the singular apart (no plural forms, as in
    // projectEnvironments/translation.ts).
    'initReport.title': 'Modules',
    'initReport.subtitle': 'Last run of the database initialisation job',
    'initReport.status.succeeded': 'Succeeded',
    'initReport.status.failed': 'Failed',
    'initReport.finishedAgo': '{{age}} ago',
    'initReport.timesTitle': 'Started {{started}} · finished {{finished}}',
    'initReport.duration': 'took {{duration}}',
    'initReport.mode.init': 'First initialisation',
    'initReport.mode.update': 'Update',
    'initReport.imageName': 'Image {{image}}',
    'initReport.installed': 'Installed',
    'initReport.updated': 'Updated',
    'initReport.none': 'none',
    'initReport.othersOne': '+ 1 other',
    'initReport.others': '+ {{n}} others',
    'initReport.custom': 'Custom modules ({{n}})',
    'initReport.customHashTitle': 'Fingerprint of the custom modules: {{hash}}',
    'initReport.state.installed': 'installed',
    'initReport.state.toInstall': 'to install',
    'initReport.state.toUpgrade': 'to upgrade',
    'initReport.state.toRemove': 'to uninstall',
    'initReport.state.absent': 'absent',
    'initReport.state.uninstallable': 'not installable',
    'initReport.logTail': 'Last lines of the log',
    'initReport.status.running': 'Running',
    'initReport.status.interrupted': 'Interrupted',
    'initReport.runningSince': 'for {{age}}',
    'initReport.startedAgo': 'started {{age}} ago',
    'initReport.startedTitle': 'Started {{started}}',
    'initReport.interrupted':
      'The job stopped without writing its final report.',
    'initReport.liveLog': 'View the live log',
    'initReport.plannedInstall': 'To install',
    'initReport.plannedUpdate': 'To update',
    'initReport.banner.running':
      'Modules are being updated: Odoo will be available again once it is done.',
    'initReport.banner.starting':
      'Odoo is starting: its pods are waiting for the database or for the initialisation to finish.',
    'initReport.banner.showModules': 'View the modules',
    // The « Configuration » tab of an Odoo environment
    // (odooConfiguration/ConfigurationTab.tsx): its Odoo words, moved from
    // projectEnvironments/translation.ts at step 4 of the genericity plan,
    // keys and texts unchanged; its shared words stay in the core's register.
    'configuration.lockEphemeral':
      'Effective configuration of this ephemeral branch: dev’s resources, without high availability (one Odoo pod, one PostgreSQL instance, no Valkey). Read-only.',
    'configuration.capacityDescription':
      'Profile of each Odoo pod and PostgreSQL instance; the fine values below.',
    'configuration.odooSection': 'Odoo',
    'configuration.databaseSection': 'PostgreSQL & volumes',
    'configuration.workersHint': 'never 1; prefork raises the memory ceiling',
    'configuration.odooCpu': 'Odoo — CPU reserved / ceiling',
    'configuration.odooMemory': 'Odoo — memory reserved / ceiling',
    'configuration.filestoreHint':
      'grow only; declarative size (nfs-client, no quota): not a reservation',
    // In place of the filestore's size, with the attachments on S3.
    'configuration.attachments': 'Attachments',
    'configuration.attachmentsS3': 'on S3 (versioned bucket)',
    'configuration.autoscalingHint': 'requires Valkey',
    'configuration.cacheForced': 'forced by the autoscaling',
    'configuration.singlePod': 'one Odoo pod (no autoscaling)',
    'configuration.restartRolling': 'rolling restart of the pods',
    'configuration.restartRecreate':
      'pods restarted, a short interruption without Valkey sessions',
    'configuration.problemWorkers': '{{field}}: allowed values {{allowed}}.',
    'configuration.problemConnections':
      'Too many Odoo processes for PostgreSQL: at least {{floor}} connections, above the {{max}} of max_connections.',
    'configuration.field.workers': 'Odoo — workers',
    'configuration.field.odooCpuRequest': 'Odoo — CPU reserved',
    'configuration.field.odooCpuLimit': 'Odoo — CPU ceiling',
    'configuration.field.odooMemoryRequest': 'Odoo — memory reserved',
    'configuration.field.odooMemoryLimit': 'Odoo — memory ceiling',
    'configuration.field.filestoreSize': 'Odoo files (filestore)',
    'configuration.field.dbCpuRequest': 'PostgreSQL — CPU reserved',
    'configuration.field.dbCpuLimit': 'PostgreSQL — CPU ceiling',
    'configuration.field.dbMemoryRequest': 'PostgreSQL — memory reserved',
    'configuration.field.dbMemoryLimit': 'PostgreSQL — memory ceiling',
    'configuration.field.databaseSize': 'PostgreSQL database (per instance)',
    'configuration.field.haCache': 'Valkey sessions',
    'configuration.field.haAutoscaling': 'Autoscaling (HPA)',
    'configuration.field.haMinReplicas': 'Pods minimum',
    'configuration.field.haMaxReplicas': 'Pods maximum',
    'configuration.field.haTargetCpu': 'CPU target',
    'configuration.field.haScaleDownWindow': 'Scale-down delay',
    'configuration.field.haDbMode': 'PostgreSQL',
    'configuration.field.haDbSync': 'Synchronous replication',
    // The Odoo chart's own pods (odooPods.ts), in the core's pod names
    // (genericity plan, step 6; formerly the core's `podName.*`).
    'podName.init': 'Init job',
    'podName.shell': 'Odoo shell',
    // « What does not change » of the promotion dialog, as Odoo says it
    // (OdooKeptSummary.tsx; formerly the core's `promote.kept*`). The
    // generic lines (HPA, PostgreSQL, the configuration) stay the core's.
    'promote.keptCapacity': 'capacity: Odoo {{cpu}} CPU, {{memory}} memory',
    'promote.keptWorkers': '{{workers}} Odoo workers',
    'promote.keptVolumes':
      'volumes: files {{filestore}}, database {{database}}',
    'promote.keptVolumesS3':
      'volumes: attachments on S3, database {{database}}',
    'promote.keptSinglePod': 'a single Odoo pod (no autoscaling)',
    'promote.keptCache': 'Valkey sessions',
    'promote.keptData': 'the data of {{target}}: database and files',
    'promote.keptDataS3':
      'the data of {{target}}: database and attachments on S3',
  },
});

/**
 * Les messages français. `full: true` fait d'une clé sans traduction une
 * erreur de compilation plutôt qu'un retour à l'anglais à l'exécution. Même
 * typographie que `projectEnvironments/translation.ts` (l'apostrophe
 * typographique, U+2019).
 */
const resource = createTranslationMessages({
  full: true,
  ref: odooTranslationRef,
  messages: {
    'shell.open': 'Ouvrir un Odoo shell',
    'shell.starting': 'Démarrage du shell…',
    'shell.help':
      'Tapez odoo-shell pour ouvrir le shell Odoo. Rien n’est enregistré sans env.cr.commit(). Le pod s’arrête après 30 min sans session.',
    'shell.stopping': 'Arrêt…',
    'shell.stopTooltip': 'Arrêter le shell Odoo',
    'shell.stopFor': 'Arrêter le shell Odoo {{pod}}',
    'shell.error.forbidden':
      'Vous n’avez pas le droit de démarrer ou d’arrêter le shell Odoo de cet environnement.',
    'shell.error.missing':
      'Cet environnement n’a pas de shell Odoo : son chart est antérieur à 0.9.8.',
    'shell.error.startFailed':
      'Le shell Odoo n’a pas pu être démarré. Réessayez dans un instant.',
    'shell.error.stopFailed':
      'Le shell Odoo n’a pas pu être arrêté. Réessayez dans un instant.',
    'shell.error.stopTimeout':
      'Le pod du shell Odoo est toujours là 1 minute après l’arrêt. Actualisez la page dans un instant.',
    'shell.error.timeout':
      'Le shell Odoo n’est toujours pas prêt après 2 minutes. Consultez la carte « À surveiller », puis réessayez.',
    'report.shell': 'Shell Odoo',
    'odooVersion.label': 'Odoo {{series}}',
    'odooVersion.build': 'Build du {{date}} ({{raw}})',
    'odooVersion.headerLabel': 'Version',
    'initReport.title': 'Modules',
    'initReport.subtitle':
      'Dernière exécution du job d’initialisation de la base',
    'initReport.status.succeeded': 'Réussie',
    'initReport.status.failed': 'Échouée',
    'initReport.finishedAgo': 'il y a {{age}}',
    'initReport.timesTitle':
      'Commencée le {{started}} · terminée le {{finished}}',
    'initReport.duration': 'durée {{duration}}',
    'initReport.mode.init': 'Première initialisation',
    'initReport.mode.update': 'Mise à jour',
    'initReport.imageName': 'Image {{image}}',
    'initReport.installed': 'Installés',
    'initReport.updated': 'Mis à jour',
    'initReport.none': 'aucun',
    'initReport.othersOne': '+ 1 autre',
    'initReport.others': '+ {{n}} autres',
    'initReport.custom': 'Modules custom ({{n}})',
    'initReport.customHashTitle': 'Empreinte des modules custom : {{hash}}',
    'initReport.state.installed': 'installé',
    'initReport.state.toInstall': 'à installer',
    'initReport.state.toUpgrade': 'à mettre à jour',
    'initReport.state.toRemove': 'à désinstaller',
    'initReport.state.absent': 'absent',
    'initReport.state.uninstallable': 'non installable',
    'initReport.logTail': 'Dernières lignes du log',
    'initReport.status.running': 'En cours',
    'initReport.status.interrupted': 'Interrompue',
    'initReport.runningSince': 'depuis {{age}}',
    'initReport.startedAgo': 'commencée il y a {{age}}',
    'initReport.startedTitle': 'Commencée le {{started}}',
    'initReport.interrupted':
      'Le job s’est arrêté sans écrire son rapport final.',
    'initReport.liveLog': 'Voir le log en direct',
    'initReport.plannedInstall': 'À installer',
    'initReport.plannedUpdate': 'À mettre à jour',
    'initReport.banner.running':
      'Mise à jour des modules en cours : Odoo sera de nouveau accessible à la fin.',
    'initReport.banner.starting':
      'Odoo démarre : ses pods attendent la base ou la fin de l’initialisation.',
    'initReport.banner.showModules': 'Voir les modules',
    'configuration.lockEphemeral':
      'Configuration effective de cette branche éphémère : les ressources de dev, sans haute disponibilité (un pod Odoo, une instance PostgreSQL, sans Valkey). Lecture seule.',
    'configuration.capacityDescription':
      'Profil de chaque pod Odoo et instance PostgreSQL ; les valeurs fines ci-dessous.',
    'configuration.odooSection': 'Odoo',
    'configuration.databaseSection': 'PostgreSQL et volumes',
    'configuration.workersHint':
      'jamais 1 ; en prefork, le plafond mémoire est relevé d’office',
    'configuration.odooCpu': 'Odoo — CPU réservé / plafond',
    'configuration.odooMemory': 'Odoo — mémoire réservée / plafond',
    'configuration.filestoreHint':
      'agrandissement seulement ; taille déclarative (nfs-client, sans quota) : pas une réservation',
    'configuration.attachments': 'Pièces jointes',
    'configuration.attachmentsS3': 'sur S3 (bucket versionné)',
    'configuration.autoscalingHint': 'impose Valkey',
    'configuration.cacheForced': 'imposées par l’autoscaling',
    'configuration.singlePod': 'un pod Odoo (sans autoscaling)',
    'configuration.restartRolling': 'redémarrage progressif des pods',
    'configuration.restartRecreate':
      'redémarrage des pods, courte coupure sans sessions Valkey',
    'configuration.problemWorkers': '{{field}} : valeurs permises {{allowed}}.',
    'configuration.problemConnections':
      'Trop de process Odoo pour PostgreSQL : au moins {{floor}} connexions, au-delà des {{max}} de max_connections.',
    'configuration.field.workers': 'Odoo — workers',
    'configuration.field.odooCpuRequest': 'Odoo — CPU réservé',
    'configuration.field.odooCpuLimit': 'Odoo — CPU plafond',
    'configuration.field.odooMemoryRequest': 'Odoo — mémoire réservée',
    'configuration.field.odooMemoryLimit': 'Odoo — mémoire plafond',
    'configuration.field.filestoreSize': 'Fichiers Odoo (filestore)',
    'configuration.field.dbCpuRequest': 'PostgreSQL — CPU réservé',
    'configuration.field.dbCpuLimit': 'PostgreSQL — CPU plafond',
    'configuration.field.dbMemoryRequest': 'PostgreSQL — mémoire réservée',
    'configuration.field.dbMemoryLimit': 'PostgreSQL — mémoire plafond',
    'configuration.field.databaseSize': 'Base PostgreSQL (par instance)',
    'configuration.field.haCache': 'Sessions Valkey',
    'configuration.field.haAutoscaling': 'Autoscaling (HPA)',
    'configuration.field.haMinReplicas': 'Pods minimum',
    'configuration.field.haMaxReplicas': 'Pods maximum',
    'configuration.field.haTargetCpu': 'Cible CPU',
    'configuration.field.haScaleDownWindow': 'Délai de réduction',
    'configuration.field.haDbMode': 'PostgreSQL',
    'configuration.field.haDbSync': 'Réplication synchrone',
    'podName.init': 'Job d’init',
    'podName.shell': 'Odoo shell',
    'promote.keptCapacity':
      'capacité : Odoo {{cpu}} CPU, {{memory}} de mémoire',
    'promote.keptWorkers': '{{workers}} workers Odoo',
    'promote.keptVolumes':
      'volumes : fichiers {{filestore}}, base {{database}}',
    'promote.keptVolumesS3':
      'volumes : pièces jointes sur S3, base {{database}}',
    'promote.keptSinglePod': 'un seul pod Odoo (sans autoscaling)',
    'promote.keptCache': 'sessions Valkey',
    'promote.keptData': 'les données de {{target}} : base et fichiers',
    'promote.keptDataS3':
      'les données de {{target}} : base et pièces jointes sur S3',
  },
});

/**
 * `translation:app/odoo-fr`, dans le module Odoo (`odooAppModule`, sur le
 * plugin `app` : TranslationBlueprint lui est réservé).
 */
export const odooFrTranslation = TranslationBlueprint.make({
  name: 'odoo-fr',
  params: { resource },
});
